import { resolveCreateDemandId } from './demandId.js';
import { paymentStructureForPlan } from './installmentPlans.js';
import { normalizeSettlementChoice } from './settlementChoice.js';
import {
  buildDemoBillingInvoices,
  hasHeavyInvoiceReceiptSeed,
  retainPlatformInvoices,
} from './platformInvoices.js';
import {
  DEMO_OPEN_CHARGE_INDEX,
  DEMO_UNPAID_CHARGE_INDEX,
  filterRecentPlatformCharges,
  hasChargeStatusDemoSeed,
} from './recentPlatformCharges.js';

const SETTINGS_KEY = 'demandjet.settings';

export const CLIENT_ROLES = Object.freeze([
  { id: 'OWNER', label: 'Owner', description: 'Every tab, including billing and users.' },
  { id: 'ADMIN', label: 'Admin', description: 'Every tab except billing.' },
  { id: 'MEMBER', label: 'Member', description: 'Demands, debtors, payments, evidence, and reports.' },
  { id: 'BILLING', label: 'Billing', description: 'Billing, payments, and reports.' },
  { id: 'VIEWER', label: 'Viewer', description: 'Demands, debtors, payments, evidence, and reports, read only.' },
]);

/** Official tabs a teammate role can open. Home, Messages, Profile, Security, Notifications, and Guides stay open to everyone. */
export const ROLE_AREA_LABELS = Object.freeze({
  demands: 'Demands',
  debtors: 'Debtors',
  payments: 'Payments',
  evidence: 'Evidence',
  reports: 'Reports',
  billing: 'Billing',
  users: 'Users',
  integrations: 'Integrations',
  automations: 'Automations',
});

const ROLE_AREAS = Object.freeze(Object.keys(ROLE_AREA_LABELS));

export const ROLE_RESTRICTIONS = Object.freeze({
  OWNER: ROLE_AREAS,
  ADMIN: ['demands', 'debtors', 'payments', 'evidence', 'reports', 'users', 'integrations', 'automations'],
  MEMBER: ['demands', 'debtors', 'payments', 'evidence', 'reports'],
  BILLING: ['billing', 'payments', 'reports'],
  VIEWER: ['demands', 'debtors', 'payments', 'evidence', 'reports'],
});

const NAV_ROLE_AREA = Object.freeze({
  demands: 'demands',
  debtors: 'debtors',
  payments: 'payments',
  'evidence-locker': 'evidence',
  reports: 'reports',
});

export function roleAllows(roleId, areaId) {
  const role = String(roleId || 'OWNER').toUpperCase();
  const areas = ROLE_RESTRICTIONS[role] || ROLE_RESTRICTIONS.OWNER;
  return areas.includes(areaId);
}

export function roleCanWrite(roleId) {
  return String(roleId || 'OWNER').toUpperCase() !== 'VIEWER';
}

/** Primary nav ids with no entry stay available (Home, Messages, Profile, Guides). */
export function roleCanOpenNav(roleId, navId) {
  const area = NAV_ROLE_AREA[navId];
  if (!area) return true;
  return roleAllows(roleId, area);
}

/** Personal settings stay available. Account tabs follow the role. */
export function roleCanOpenSettingsTab(roleId, tabId) {
  const id = tabId === 'connections' ? 'integrations' : tabId;
  if (!id || id === 'profile' || id === 'notifications' || id === 'security') return true;
  return roleAllows(roleId, id);
}

export function settingsPathForRole(roleId) {
  if (roleAllows(roleId, 'billing')) return '/client/settings?tab=billing';
  if (roleAllows(roleId, 'users')) return '/client/settings?tab=users';
  if (roleAllows(roleId, 'integrations')) return '/client/settings?tab=integrations';
  if (roleAllows(roleId, 'automations')) return '/client/settings?tab=automations';
  return '/client/settings?tab=notifications';
}

/**
 * Role for the signed-in email.
 * A role on someone else's people list wins over a shell account where this
 * person is only the owner. The largest other roster is the company account.
 * Unknown emails keep full access so an account holder is not locked out.
 */
export function orgRoleForEmail(email) {
  const key = String(email || '').trim().toLowerCase();
  if (!key) return 'OWNER';
  const all = readAll();
  const joined = String(all[key]?.joinedOrganization?.role || '').toUpperCase();
  if (ROLE_RESTRICTIONS[joined] && joined !== 'OWNER') return joined;
  let best = null;
  for (const [accountEmail, settings] of Object.entries(all)) {
    const roster = Array.isArray(settings?.orgUsers) ? settings.orgUsers : [];
    const match = roster.find((person) => String(person?.email || '').trim().toLowerCase() === key);
    if (!match?.role) continue;
    const role = String(match.role).toUpperCase();
    const ownsAccount = String(accountEmail || '').toLowerCase() === key;
    if (ownsAccount || role === 'OWNER') continue;
    if (!best || roster.length > best.size) best = { role, size: roster.length };
  }
  if (best?.role) return best.role;
  return 'OWNER';
}

export const PIPELINE_NOTIFICATIONS = Object.freeze([
  {
    id: 'attorneyAssigned',
    label: 'Attorney Assigned',
    description: 'An attorney has been assigned to your case and is drafting the demand.',
  },
  {
    id: 'demandMailed',
    label: 'Demand Sent',
    description: 'The demand letter has been sent to your debtor via certified mail.',
  },
  {
    id: 'debtorReceived',
    label: 'Demand Delivered',
    description:
      'Proof of delivery is confirmed via certified receipt, tracking, or digital read receipt.',
  },
  {
    id: 'debtorResponded',
    label: 'Debtor responded',
    description: 'Debtor has responded to a demand.',
  },
  {
    id: 'settlementOffer',
    label: 'Settlement offer',
    description: 'You received a settlement offer on a demand.',
  },
  {
    id: 'paymentPlan',
    label: 'Structured payment plan',
    description: 'Debtor agreed to a structured payment plan.',
  },
  {
    id: 'debtorPaid',
    label: 'Resolved - Paid',
    description:
      'The debtor has fulfilled the payment requirement; the case is successfully closed.',
  },
  {
    id: 'unpaid',
    label: 'Unpaid',
    description:
      'The demand deadline passed without payment, and the case is ready for litigation.',
  },
]);

export const CURRENCIES = Object.freeze([
  { value: 'USD', label: 'USA (USD)' },
  { value: 'CAD', label: 'Canada (CAD)' },
  { value: 'GBP', label: 'United Kingdom (GBP)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'AUD', label: 'Australia (AUD)' },
  { value: 'NZD', label: 'New Zealand (NZD)' },
]);

export const LANGUAGES = Object.freeze([
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-CA', label: 'English (Canada)' },
  { value: 'fr-CA', label: 'French (Canada)' },
]);

/** Compose structured address parts into a single line (mailing / legacy API). */
export function formatAddress({
  addressStreet = '',
  addressCity = '',
  addressState = '',
  addressPostalCode = '',
} = {}) {
  const line1 = String(addressStreet || '').trim();
  const city = String(addressCity || '').trim();
  const region = String(addressState || '').trim();
  const postal = String(addressPostalCode || '').trim();
  const line2 = [city, region].filter(Boolean).join(', ');
  const tail = [line2, postal].filter(Boolean).join(' ');
  return [line1, tail].filter(Boolean).join('\n');
}

/**
 * Normalize settings so structured address fields exist.
 * Legacy single `address` blobs are kept in street until the user edits.
 */
export function normalizeAddressFields(settings = {}) {
  const street = String(settings.addressStreet || '').trim();
  const city = String(settings.addressCity || '').trim();
  const state = String(settings.addressState || '').trim();
  const postal = String(settings.addressPostalCode || '').trim();
  const legacy = String(settings.address || '').trim();

  const addressStreet = street || (!city && !state && !postal ? legacy : '');
  const parts = {
    addressStreet,
    addressCity: city,
    addressState: state,
    addressPostalCode: postal,
  };
  return {
    ...parts,
    address: formatAddress(parts) || legacy,
  };
}

export const SYNC_CADENCES = Object.freeze([
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'BIWEEKLY', label: 'Every 2 weeks' },
]);

export const INTEGRATION_CATALOG = Object.freeze([
  {
    id: 'QUICKBOOKS',
    name: 'QuickBooks',
    description:
      'Import open invoices as automated demands and mark them Paid in QuickBooks after collection.',
    capabilities: ['import_invoices', 'mark_paid', 'send_demands'],
  },
  {
    id: 'ZOHO_BOOKS',
    name: 'Zoho Books',
    description: 'Sync open invoices and mark origin invoices paid in Zoho Books after collection.',
    capabilities: ['import_invoices', 'mark_paid'],
  },
  {
    id: 'XERO',
    name: 'Xero',
    description: 'Connect Xero to import unpaid invoices and write back payment status.',
    capabilities: ['import_invoices', 'mark_paid'],
  },
  {
    id: 'SAGE',
    name: 'Sage',
    description: 'Link Sage Business Cloud Accounting for invoice import and paid reconciliation.',
    capabilities: ['import_invoices', 'mark_paid'],
  },
  {
    id: 'NETSUITE',
    name: 'Oracle NetSuite',
    description: 'Connect Oracle NetSuite to sync invoices and apply customer payments.',
    capabilities: ['import_invoices', 'mark_paid'],
  },
  {
    id: 'FRESHBOOKS',
    name: 'FreshBooks',
    description: 'Import FreshBooks invoices and mark them paid when DemandJet collects.',
    capabilities: ['import_invoices', 'mark_paid'],
  },
  {
    id: 'WAVE',
    name: 'Wave Accounting',
    description: 'Connect Wave to pull open invoices and reconcile payments after collection.',
    capabilities: ['import_invoices', 'mark_paid'],
  },
]);

/** Monthly add-on pricing for accounting connectors (USD). Flat per connection. */
export const INTEGRATION_PRICING = Object.freeze({
  currency: 'USD',
  /** Flat monthly price for each active accounting connection. */
  connectionMonthly: 19,
});

/** Monthly add-on pricing for org users beyond the included account owner. */
export const SEAT_ADDON_PRICING = Object.freeze({
  currency: 'USD',
  includedSeats: 1,
  extraSeatMonthly: 7,
});

/**
 * Published per accepted-demand fee tiers (USD). Charged when an attorney accepts.
 * Rate is set from trailing 3-month accepted-demand volume — customers do not pick a tier.
 */
export const DEMAND_FEE_TIERS = Object.freeze([
  {
    id: 'tier1',
    label: 'Tier 1',
    perDemandPrice: 249,
    priced: true,
    minAccepted: 1,
    maxAccepted: 5,
    description: '1–5 accepted demands / mo',
  },
  {
    id: 'tier2',
    label: 'Tier 2',
    perDemandPrice: 229,
    priced: true,
    minAccepted: 6,
    maxAccepted: 15,
    description: '6–15 accepted demands / mo',
  },
  {
    id: 'tier3',
    label: 'Tier 3',
    perDemandPrice: 199,
    priced: true,
    minAccepted: 16,
    maxAccepted: 30,
    description: '16–30 accepted demands / mo',
  },
  {
    id: 'tier4',
    label: 'Tier 4',
    perDemandPrice: 179,
    priced: true,
    minAccepted: 31,
    maxAccepted: null,
    description: '31+ accepted demands / mo',
  },
]);

export const DEMAND_FEE_CURRENCY = 'USD';

/** Demo / fallback trailing average when billing has no live volume yet. */
export const DEMAND_FEE_DEMO_TRAILING_AVG = 4.2;

export function getDemandFeeTiers() {
  return DEMAND_FEE_TIERS.map((tier) => ({ ...tier }));
}

export function demandFeeVolumeLabel(tier) {
  if (!tier) return '';
  if (tier.maxAccepted == null) {
    return `${tier.minAccepted}+ accepted demands / mo`;
  }
  return `${tier.minAccepted}–${tier.maxAccepted} accepted demands / mo`;
}

export function resolveDemandFeeTierFromAverage(avg) {
  const n = Number(avg);
  if (!Number.isFinite(n) || n < 6) return 'tier1';
  if (n < 16) return 'tier2';
  if (n < 31) return 'tier3';
  return 'tier4';
}

/** Map legacy low/high ids (and unknown values) onto published tier ids. */
export function normalizeDemandFeeTierId(tierId) {
  const id = String(tierId || '').toLowerCase();
  if (id === 'low') return 'tier1';
  if (id === 'high') return 'tier3';
  if (DEMAND_FEE_TIERS.some((tier) => tier.id === id)) return id;
  return 'tier1';
}

export function getDemandFeeTierById(tierId) {
  const id = normalizeDemandFeeTierId(tierId);
  return DEMAND_FEE_TIERS.find((tier) => tier.id === id) || DEMAND_FEE_TIERS[0];
}

export function resolveDemandFeeTrailingAvg(billing = {}) {
  const avg = Number(billing?.demandFeeTrailingAvg);
  if (Number.isFinite(avg) && avg >= 0) return avg;
  return DEMAND_FEE_DEMO_TRAILING_AVG;
}

export function resolveDemandFeeTierId(billing = {}) {
  // Published rate is volume-driven; trailing average (with demo fallback) wins.
  return resolveDemandFeeTierFromAverage(resolveDemandFeeTrailingAvg(billing));
}

export function getDemandFeeUsage(billing = {}) {
  const trailingAvg = resolveDemandFeeTrailingAvg(billing);
  const tierId = resolveDemandFeeTierFromAverage(trailingAvg);
  const tier = getDemandFeeTierById(tierId);
  const nextTier =
    DEMAND_FEE_TIERS.find(
      (candidate) =>
        candidate.minAccepted > (tier.maxAccepted == null ? Infinity : tier.maxAccepted)
    ) || null;
  const nextThreshold = nextTier?.minAccepted ?? null;
  const meterPct =
    nextThreshold == null
      ? 100
      : Math.max(4, Math.min(100, Math.round((trailingAvg / nextThreshold) * 100)));
  const avgLabel =
    Number.isInteger(trailingAvg) ? String(trailingAvg) : trailingAvg.toFixed(1);
  const nextHint = nextTier
    ? `You’ll stay on ${tier.label} next month unless that rises to ${nextThreshold} or more.`
    : `You’re on ${tier.label} — the highest published rate.`;
  return {
    trailingAvg,
    avgLabel,
    tierId,
    tierLabel: tier.label,
    nextTier,
    nextThreshold,
    meterPct,
    headline: `You’re averaging ${avgLabel} accepted demands per month`,
    hint: nextHint,
  };
}

export function getDemandFeeEntitlement(billing = {}) {
  const tier = getDemandFeeTierById(resolveDemandFeeTierId(billing));
  const usage = getDemandFeeUsage(billing);
  return {
    tierId: tier.id,
    tierLabel: tier.label,
    perDemandPrice: tier.perDemandPrice,
    priced: tier.priced,
    currency: DEMAND_FEE_CURRENCY,
    description: tier.description || demandFeeVolumeLabel(tier),
    volumeLabel: demandFeeVolumeLabel(tier),
    minAccepted: tier.minAccepted,
    maxAccepted: tier.maxAccepted,
    chargeTiming: 'attorney_accept',
    recoverySharePercent: 0,
    trailingAvg: usage.trailingAvg,
    usage,
  };
}

export function formatDemandFeePrice(amount) {
  if (amount == null || !Number.isFinite(Number(amount))) return 'Contact us';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: DEMAND_FEE_CURRENCY,
    maximumFractionDigits: 0,
  }).format(Number(amount));
}

export function setDemandFeeTier(billing = {}, tierId = 'tier1') {
  const tier = getDemandFeeTierById(tierId);
  return {
    ...billing,
    demandFeeTierId: tier.id,
    demandFeePerDemandPrice: tier.perDemandPrice,
  };
}

export function formatIntegrationPrice(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: INTEGRATION_PRICING.currency,
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);
}

export function formatSeatAddonPrice(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: SEAT_ADDON_PRICING.currency,
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);
}

/**
 * Assign billed amounts to connections: every active connector is $19/mo.
 * Available connectors show the same add-on price to connect them.
 */
export function integrationPricingForConnections(connections = []) {
  const list = Array.isArray(connections) ? connections : [];
  const unit = INTEGRATION_PRICING.connectionMonthly;
  const connected = list.filter((c) => c.status === 'connected');
  const monthlyTotal = connected.length * unit;

  return {
    connectedCount: connected.length,
    monthlyTotal,
    nextPrice: unit,
    unitPrice: unit,
    byId: Object.fromEntries(
      list.map((conn) => {
        if (conn.status === 'connected') {
          return [
            conn.id,
            {
              amount: unit,
              label: `${formatIntegrationPrice(unit)} / mo`,
              kind: 'connection',
            },
          ];
        }
        return [
          conn.id,
          {
            amount: unit,
            label:
              connected.length === 0
                ? `${formatIntegrationPrice(unit)} / mo`
                : `+${formatIntegrationPrice(unit)} / mo`,
            kind: 'connection',
          },
        ];
      })
    ),
  };
}

/**
 * Extra-user add-on: account owner (1 seat) is included; each additional user is $7/mo.
 */
export function seatAddonPricingForUsers(orgUsers = []) {
  const list = Array.isArray(orgUsers) ? orgUsers : [];
  const totalUsers = list.length;
  const includedSeats = SEAT_ADDON_PRICING.includedSeats;
  const extraSeats = Math.max(0, totalUsers - includedSeats);
  const unit = SEAT_ADDON_PRICING.extraSeatMonthly;
  const monthlyTotal = extraSeats * unit;
  return {
    totalUsers,
    includedSeats,
    extraSeats,
    unitPrice: unit,
    monthlyTotal,
    nextInvitePrice: unit,
    label:
      extraSeats === 0
        ? 'Owner seat included · no extra-user charges'
        : `${extraSeats} extra user${extraSeats === 1 ? '' : 's'}`,
  };
}

function seatCountLabel(count) {
  const seats = Math.max(0, Number(count) || 0);
  return `${seats} ${seats === 1 ? 'seat' : 'seats'}`;
}

/** Purchase check before an invite adds a $7/mo seat. */
export function seatInviteConfirm(orgUsers = [], invite = {}) {
  const now = seatAddonPricingForUsers(orgUsers);
  const next = seatAddonPricingForUsers([...(Array.isArray(orgUsers) ? orgUsers : []), invite]);
  const roleId = String(invite.role || 'MEMBER').toUpperCase();
  const role = CLIENT_ROLES.find((item) => item.id === roleId);
  const person = String(invite.name || invite.email || '').trim();
  return {
    title: 'Add a seat',
    action: 'Send invite',
    person,
    email: String(invite.email || '').trim(),
    roleLabel: role?.label || 'Member',
    nowLabel: seatCountLabel(now.totalUsers),
    nowPriceLabel: `${formatSeatAddonPrice(now.monthlyTotal)}/mo`,
    nextLabel: seatCountLabel(next.totalUsers),
    nextPriceLabel: `${formatSeatAddonPrice(next.monthlyTotal)}/mo`,
    unitPriceLabel: `${formatSeatAddonPrice(SEAT_ADDON_PRICING.extraSeatMonthly)}/mo`,
    deltaLabel: `+${formatSeatAddonPrice(SEAT_ADDON_PRICING.extraSeatMonthly)}/mo`,
    nextDetail: `+${formatSeatAddonPrice(SEAT_ADDON_PRICING.extraSeatMonthly)}/mo · ${formatSeatAddonPrice(next.monthlyTotal)}/mo total`,
    note: 'This seat is added to the bill when the invite is sent.',
  };
}

/** Check before Remove drops a teammate and their seat charge. */
export function seatRemoveConfirm(orgUsers = [], person = {}) {
  const list = Array.isArray(orgUsers) ? orgUsers : [];
  const now = seatAddonPricingForUsers(list);
  const next = seatAddonPricingForUsers(list.filter((user) => user?.id !== person?.id));
  const roleId = String(person.role || 'MEMBER').toUpperCase();
  const role = CLIENT_ROLES.find((item) => item.id === roleId);
  const invited = String(person.status || '').toUpperCase() === 'INVITED';
  const unit = formatSeatAddonPrice(SEAT_ADDON_PRICING.extraSeatMonthly);
  return {
    title: invited ? 'Remove this invite?' : 'Remove this person?',
    action: 'Remove',
    person: String(person.name || person.email || '').trim(),
    email: String(person.email || '').trim(),
    roleLabel: role?.label || 'Member',
    nowLabel: seatCountLabel(now.totalUsers),
    nowPriceLabel: `${formatSeatAddonPrice(now.monthlyTotal)}/mo`,
    nextLabel: seatCountLabel(next.totalUsers),
    nextPriceLabel: `${formatSeatAddonPrice(next.monthlyTotal)}/mo`,
    unitPriceLabel: `${unit}/mo`,
    deltaLabel: `-${unit}/mo`,
    note: invited
      ? 'This invite stops working, and the seat comes off the bill.'
      : 'They lose access to this account, and the seat comes off the bill.',
  };
}

export const INTEGRATION_IDS = Object.freeze(INTEGRATION_CATALOG.map((p) => p.id));

function readAll() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeAll(all) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(all));
}

export function defaultNotifications() {
  return {
    emailEnabled: true,
    monthlyBillingStatement: true,
    pipeline: {
      attorneyAssigned: true,
      demandMailed: true,
      debtorReceived: true,
      debtorResponded: true,
      settlementOffer: true,
      paymentPlan: true,
      debtorPaid: true,
      unpaid: true,
    },
    newSignInAlert: true,
    thirdPartyAppAlert: true,
    integrationSyncAlert: true,
    paymentFailure: true,
    newMessage: true,
    newNote: true,
    evidenceLockerNearingLimit: true,
    phoneNumber: '',
  };
}

export function defaultSecurity() {
  return {
    mfaEnabled: false,
    mfaMethod: 'NONE', // NONE | AUTHENTICATOR | SMS
    recoveryEmail: '',
    recoveryPhone: '',
    lastPasswordChangeAt: null,
  };
}

export function defaultDemandDefaults() {
  return {
    installmentPlan: 'NO',
    settlementChoice: 'NO',
  };
}

export function normalizeDemandDefaults(value) {
  const installmentPlan = String(value?.installmentPlan || '').trim().toUpperCase() === 'YES' ? 'YES' : 'NO';
  return {
    installmentPlan,
    settlementChoice: normalizeSettlementChoice(value?.settlementChoice),
  };
}

/** Matter columns stamped onto demands created from a connected app. */
export function demandChoiceFields(settings) {
  const defaults = normalizeDemandDefaults(settings?.demandDefaults);
  return {
    Installment_Plan: defaults.installmentPlan,
    Payment_Structure: paymentStructureForPlan(defaults.installmentPlan),
    Settlement_Choice: defaults.settlementChoice,
  };
}

export function defaultAutomations() {
  return {
    accountingSyncEnabled: false,
    accountingSyncCadence: 'WEEKLY',
    paymentTermsEnabled: false,
    autoApplyPaymentTerms: false,
    automatedReminders: true,
    creditNoteNotification: true,
    /** When true, paid demands (including paid-before-assignment) are archived automatically. */
    autoArchivePaidDemands: false,
  };
}

export function defaultBilling() {
  return {
    allowAddPaymentMethod: true,
    /** @deprecated Invoices tab always shows retained monthly invoices. */
    showMonthlyInvoices: true,
    showPaymentsAndReceipts: true,
    paymentMethods: [],
    invoices: [],
    payments: [],
    /** @deprecated Demand plans removed; archive/active/seats are unlimited. */
    archiveFreeLimit: null,
    /** @deprecated Active demands are unlimited. */
    activeDemandFreeLimit: null,
    /** @deprecated Demand plans removed. */
    archiveStoragePlanId: 'free',
    /** Extra user seats beyond the included owner (billed at SEAT_ADDON_PRICING). */
    extraSeats: 0,
    /** @deprecated Owner is the only included seat; extras are a $7 add-on. */
    seatFreeLimit: 1,
    /** @deprecated Demand plans removed. */
    archiveStorageEnabled: false,
    archiveStorageEnabledAt: null,
    archiveStorageStripePriceId: null,
    archiveStorageStripeItemId: null,
    archiveStorageStripeSubscriptionId: null,
    /** Per-demand fee tier id (tier1–tier4). Driven by trailing volume when set. */
    demandFeeTierId: 'tier1',
    demandFeePerDemandPrice: 249,
    /** Trailing 3-month average of accepted demands / mo (sets published tier). */
    demandFeeTrailingAvg: DEMAND_FEE_DEMO_TRAILING_AVG,
    /** Evidence Locker add-on: included | starter | growth | scale */
    evidenceVaultPlanId: 'included',
    evidenceVaultEnabled: false,
    evidenceVaultEnabledAt: null,
    evidenceVaultStripePriceId: null,
    evidenceVaultStripeItemId: null,
    evidenceVaultStripeSubscriptionId: null,
  };
}

export function defaultPaymentTerms() {
  return [
    {
      id: 'pt_net30',
      name: 'Net 30',
      daysDue: 30,
      description: 'Payment due within 30 days of invoice.',
      isDefault: true,
      active: true,
    },
    {
      id: 'pt_net15',
      name: 'Net 15',
      daysDue: 15,
      description: 'Payment due within 15 days of invoice.',
      isDefault: false,
      active: true,
    },
  ];
}

export function defaultOrgUsers(ownerEmail, ownerName) {
  return [
    {
      id: 'u_owner',
      email: String(ownerEmail || '').toLowerCase(),
      name: String(ownerName || '').trim(),
      role: 'OWNER',
      status: 'ACTIVE',
      invitedAt: new Date().toISOString(),
    },
  ];
}

/** Sample teammates so the people list shows a role menu and a pending invite. */
export function demoTeammates(ownerEmail) {
  const domain = String(ownerEmail || '').split('@')[1] || 'example.com';
  return [
    {
      id: 'u_demo_admin',
      email: `maya@${domain}`,
      name: 'Maya Chen',
      role: 'ADMIN',
      status: 'ACTIVE',
      invitedAt: '2026-09-12T15:00:00.000Z',
    },
    {
      id: 'u_demo_member',
      email: `jordan@${domain}`,
      name: 'Jordan Lee',
      role: 'MEMBER',
      status: 'INVITED',
      invitedAt: '2026-10-02T15:00:00.000Z',
    },
    {
      id: 'u_demo_billing',
      email: `sam@${domain}`,
      name: 'Sam Patel',
      role: 'BILLING',
      status: 'ACTIVE',
      invitedAt: '2026-10-18T15:00:00.000Z',
    },
  ];
}

/**
 * Accounts that only have the owner get three sample teammates once.
 * Removing a teammate sticks. A later list that already has teammates is left as saved.
 */
export function ensureDemoOrgUsers(orgUsers, email, { seeded = false } = {}) {
  const list = Array.isArray(orgUsers) ? orgUsers.filter(Boolean) : [];
  const extras = list.filter((user) => user.role !== 'OWNER');
  if (seeded || extras.length > 0) {
    return {
      orgUsers: list.length ? list : defaultOrgUsers(email, ''),
      demoTeammatesSeeded: true,
    };
  }
  const owner = list.find((user) => user.role === 'OWNER') || defaultOrgUsers(email, '')[0];
  const ownerEmail = String(owner.email || email || '').toLowerCase();
  const samples = demoTeammates(ownerEmail).filter((sample) => sample.email !== ownerEmail);
  return {
    orgUsers: [{ ...owner, email: ownerEmail }, ...samples],
    demoTeammatesSeeded: true,
  };
}

export function defaultSettings(email) {
  const normalized = String(email || '').toLowerCase();
  return {
    email: normalized,
    displayName: '',
    avatarDataUrl: '',
    organizationName: '',
    countryRegion: 'United States',
    addressStreet: '',
    addressCity: '',
    addressState: '',
    addressPostalCode: '',
    address: '',
    phoneNumber: '',
    currency: 'USD',
    language: 'en-US',
    licensedStates: '',
    security: defaultSecurity(),
    notifications: defaultNotifications(),
    automations: defaultAutomations(),
    billing: defaultBilling(),
    paymentTermsEnabled: false,
    autoApplyPaymentTerms: false,
    paymentTerms: defaultPaymentTerms(),
    demandDefaults: defaultDemandDefaults(),
    orgUsers: defaultOrgUsers(normalized, ''),
    shareOrganizationMessages: false,
    connectedIntegrations: {},
    bankingAccount: {
      accountName: '',
      bankName: '',
      routingNumber: '',
      accountNumberLast4: '',
      accountType: 'CHECKING',
      enabled: false,
    },
    accountingProvider: 'NONE',
    accountingConnected: false,
    accountingConnectedAt: null,
    accountingAuthToken: null,
    accountingRealmId: null,
    accountingLastSyncAt: null,
    accountingDemo: false,
  };
}

function storedNotifications(stored) {
  const next = { ...(stored?.notifications || {}) };
  delete next.paymentThankYou;
  return next;
}

export function displayPersonName(person) {
  const name = String(person?.name || '').trim();
  const email = String(person?.email || '').trim();
  if (!name || name.toLowerCase() === 'account owner') return email;
  return name;
}

export function normalizeJoinedOrganization(value) {
  const role = String(value?.role || '').trim().toUpperCase();
  const orgUsers = Array.isArray(value?.orgUsers) ? value.orgUsers.filter(Boolean) : [];
  if (!ROLE_RESTRICTIONS[role] || role === 'OWNER' || !orgUsers.length) return null;
  return {
    role,
    ownerEmail: String(value.ownerEmail || '').trim().toLowerCase(),
    orgUsers,
  };
}

/** Merge stored settings with defaults so new keys appear after upgrades. */
export function mergeSettings(email, stored = {}) {
  const base = defaultSettings(email);
  const normalized = String(email || stored.email || '').toLowerCase();
  const people = ensureDemoOrgUsers(
    Array.isArray(stored.orgUsers) ? stored.orgUsers : base.orgUsers,
    normalized,
    { seeded: Boolean(stored.demoTeammatesSeeded) }
  );
  const merged = {
    ...base,
    ...stored,
    email: normalized,
    security: { ...base.security, ...(stored.security || {}) },
    notifications: {
      ...base.notifications,
      ...storedNotifications(stored),
      paymentFailure: true,
      pipeline: {
        ...base.notifications.pipeline,
        ...((stored.notifications && stored.notifications.pipeline) || {}),
      },
    },
    automations: { ...base.automations, ...(stored.automations || {}) },
    billing: {
      ...base.billing,
      ...(stored.billing || {}),
      paymentMethods: Array.isArray(stored.billing?.paymentMethods)
        ? stored.billing.paymentMethods
        : base.billing.paymentMethods,
      invoices: Array.isArray(stored.billing?.invoices)
        ? stored.billing.invoices
        : base.billing.invoices,
      payments: Array.isArray(stored.billing?.payments)
        ? stored.billing.payments
        : base.billing.payments,
    },
    paymentTerms: Array.isArray(stored.paymentTerms)
      ? stored.paymentTerms
      : base.paymentTerms,
    demandDefaults: normalizeDemandDefaults({
      ...base.demandDefaults,
      ...(stored.demandDefaults || {}),
    }),
    orgUsers: people.orgUsers,
    demoTeammatesSeeded: people.demoTeammatesSeeded,
    joinedOrganization: normalizeJoinedOrganization(stored.joinedOrganization),
    connectedIntegrations:
      stored.connectedIntegrations && typeof stored.connectedIntegrations === 'object'
        ? stored.connectedIntegrations
        : base.connectedIntegrations,
    bankingAccount: {
      ...base.bankingAccount,
      ...(stored.bankingAccount || {}),
    },
  };
  return {
    ...merged,
    ...normalizeAddressFields(merged),
  };
}

export function getLocalSettings(email) {
  if (!email) return defaultSettings('');
  const all = readAll();
  const stored = all[email.toLowerCase()];
  return mergeSettings(email, stored || {});
}

export function saveLocalSettings(email, settings) {
  const key = String(email || '').toLowerCase();
  if (!key) return settings;
  const all = readAll();
  const merged = mergeSettings(key, settings);
  all[key] = { ...merged, email: key, updatedAt: new Date().toISOString() };
  writeAll(all);
  return all[key];
}

/**
 * Prefill empty profile fields from a social IdP (Google/Apple/…).
 * Does not overwrite values the user already saved.
 * Standard Google Sign-In does not provide organization or street address.
 */
export function seedProfileFromProvider(email, hints = {}) {
  if (!email) return defaultSettings('');
  const current = getLocalSettings(email);
  const alreadySeeded = Boolean(current.profileSeededFrom);
  const next = {
    ...current,
    displayName: current.displayName || hints.displayName || '',
    organizationName: current.organizationName || hints.organizationName || '',
    addressStreet: current.addressStreet || hints.addressStreet || '',
    addressCity: current.addressCity || hints.addressCity || '',
    addressState: current.addressState || hints.addressState || '',
    addressPostalCode: current.addressPostalCode || hints.addressPostalCode || '',
    address: current.address || hints.address || '',
    currency: alreadySeeded ? current.currency : hints.currency || current.currency || 'USD',
    language: alreadySeeded ? current.language : hints.language || current.language || 'en-US',
    countryRegion: alreadySeeded
      ? current.countryRegion
      : hints.countryRegion || current.countryRegion || 'United States',
    profileSeededFrom: current.profileSeededFrom || hints.source || 'social',
  };
  return saveLocalSettings(email, next);
}

export function connectionsFromSettings(settings) {
  const map = { ...(settings.connectedIntegrations || {}) };
  const legacyProvider = String(settings.accountingProvider || 'NONE').toUpperCase();
  const legacyConnected = Boolean(settings.accountingConnected && legacyProvider !== 'NONE');

  if (legacyConnected && !map[legacyProvider]) {
    map[legacyProvider] = {
      status: 'connected',
      connectedAt: settings.accountingConnectedAt || null,
      lastSyncAt: settings.accountingLastSyncAt || null,
      realmId: settings.accountingRealmId || null,
      demo: Boolean(settings.accountingDemo),
      primary: true,
      authToken: settings.accountingAuthToken || null,
    };
  }

  return INTEGRATION_CATALOG.map((meta) => {
    const row = map[meta.id] || {};
    const connected = row.status === 'connected';
    return {
      id: meta.id,
      name: meta.name,
      description: meta.description,
      capabilities: meta.capabilities,
      status: connected ? 'connected' : 'available',
      connectedAt: connected ? row.connectedAt || null : null,
      lastSyncAt: connected ? row.lastSyncAt || null : null,
      realmId: connected ? row.realmId || null : null,
      demo: connected ? Boolean(row.demo) : false,
      primary: connected
        ? Boolean(row.primary) || legacyProvider === meta.id
        : false,
    };
  });
}

export function connectIntegrationLocal(settings, providerId, extras = {}) {
  const id = String(providerId || '').toUpperCase();
  const connectedIntegrations = { ...(settings.connectedIntegrations || {}) };
  const connectedAt = extras.connectedAt || new Date().toISOString();
  const makePrimary =
    extras.primary === true ||
    !Object.values(connectedIntegrations).some((c) => c.status === 'connected');

  if (makePrimary) {
    for (const key of Object.keys(connectedIntegrations)) {
      connectedIntegrations[key] = { ...connectedIntegrations[key], primary: false };
    }
  }

  connectedIntegrations[id] = {
    status: 'connected',
    connectedAt,
    lastSyncAt: extras.lastSyncAt || connectedIntegrations[id]?.lastSyncAt || null,
    realmId: extras.realmId || null,
    demo: Boolean(extras.demo),
    primary: makePrimary,
    authToken: extras.authToken || extras.token || null,
  };

  return {
    ...settings,
    connectedIntegrations,
    accountingProvider: makePrimary ? id : settings.accountingProvider || id,
    accountingConnected: true,
    accountingConnectedAt: makePrimary ? connectedAt : settings.accountingConnectedAt,
    accountingRealmId: makePrimary
      ? extras.realmId || null
      : settings.accountingRealmId,
    accountingDemo: makePrimary ? Boolean(extras.demo) : settings.accountingDemo,
    accountingAuthToken: makePrimary
      ? extras.authToken || extras.token || settings.accountingAuthToken
      : settings.accountingAuthToken,
    accountingLastSyncAt: makePrimary
      ? extras.lastSyncAt || settings.accountingLastSyncAt
      : settings.accountingLastSyncAt,
  };
}

export function disconnectIntegrationLocal(settings, providerId) {
  const id = String(providerId || '').toUpperCase();
  const connectedIntegrations = { ...(settings.connectedIntegrations || {}) };
  delete connectedIntegrations[id];

  const remaining = Object.entries(connectedIntegrations).filter(
    ([, v]) => v && v.status === 'connected'
  );
  let next = {
    ...settings,
    connectedIntegrations,
  };

  if (!remaining.length) {
    next = {
      ...next,
      accountingProvider: 'NONE',
      accountingConnected: false,
      accountingConnectedAt: null,
      accountingAuthToken: null,
      accountingRealmId: null,
      accountingLastSyncAt: null,
      accountingDemo: false,
    };
  } else if (settings.accountingProvider === id) {
    const [primaryId, primary] = remaining.find(([, v]) => v.primary) || remaining[0];
    connectedIntegrations[primaryId] = { ...primary, primary: true };
    next = {
      ...next,
      connectedIntegrations,
      accountingProvider: primaryId,
      accountingConnected: true,
      accountingConnectedAt: primary.connectedAt || null,
      accountingAuthToken: primary.authToken || null,
      accountingRealmId: primary.realmId || null,
      accountingLastSyncAt: primary.lastSyncAt || null,
      accountingDemo: Boolean(primary.demo),
    };
  }

  return next;
}

export function createPaymentTerm({ name, daysDue, description, isDefault }) {
  return {
    id: `pt_${Date.now()}`,
    name: String(name || '').trim(),
    daysDue: Number(daysDue) || 30,
    description: String(description || '').trim(),
    isDefault: Boolean(isDefault),
    active: true,
  };
}

export function createOrgInvite({ email, name, role }) {
  return {
    id: `u_${Date.now()}`,
    email: String(email || '')
      .trim()
      .toLowerCase(),
    name: String(name || '').trim() || email.split('@')[0],
    role: role || 'MEMBER',
    status: 'INVITED',
    invitedAt: new Date().toISOString(),
  };
}

const DEMO_CHARGE_LABELS = Object.freeze([
  'Platform subscription',
  'Per-demand fee',
  'Integration add-on',
  'Extra user seat',
  'Evidence Locker',
]);

/** Build N succeeded platform charges spaced across the last `withinDays` days. */
export function buildDemoPlatformCharges(
  count = 100,
  { now = Date.now(), withinDays = 60 } = {}
) {
  const total = Math.max(0, Number(count) || 0);
  const windowMs = Math.max(1, Number(withinDays) || 60) * 24 * 60 * 60 * 1000;
  const payments = [];
  for (let i = 0; i < total; i += 1) {
    const t = i / Math.max(total - 1, 1);
    const at = new Date(Number(now) - Math.round(t * windowMs * 0.98));
    const label = DEMO_CHARGE_LABELS[i % DEMO_CHARGE_LABELS.length];
    const amount =
      label === 'Platform subscription' || label === 'Per-demand fee'
        ? 249
        : label === 'Integration add-on'
          ? INTEGRATION_PRICING.connectionMonthly
          : label === 'Extra user seat'
            ? 7
            : label === 'Evidence Locker'
              ? 29
              : 12 + (i % 9);
    // Seed a declined recurring charge and an in-flight retry for status UI.
    const status =
      i === DEMO_UNPAID_CHARGE_INDEX
        ? 'UNPAID'
        : i === DEMO_OPEN_CHARGE_INDEX
          ? 'OPEN'
          : 'PAID';
    const paid = status === 'PAID';
    payments.push({
      id: `pay_demo_${String(i + 1).padStart(3, '0')}`,
      label,
      amount,
      currency: 'USD',
      status,
      paidAt: paid ? at.toISOString() : null,
      attemptedAt: at.toISOString(),
      receiptUrl: paid ? '#' : null,
    });
  }
  return payments;
}

export function demoBillingRecords() {
  const now = Date.now();
  return {
    paymentMethods: [
      {
        id: 'pm_demo_visa',
        brand: 'Visa',
        last4: '4242',
        expMonth: 12,
        expYear: 2028,
        isDefault: true,
      },
    ],
    invoices: buildDemoBillingInvoices({ now }),
    payments: buildDemoPlatformCharges(100, { now, withinDays: 30 }),
  };
}

/**
 * Keep Billing demo data useful: refill invoices/charges when missing, thin, or
 * when all platform charges have aged out of the Payment Method 30-day window
 * (common after an older localStorage seed or empty server settings overwrite).
 */
export function ensureDemoBillingSeed(billing = {}, { now = Date.now() } = {}) {
  const demo = {
    paymentMethods: [
      {
        id: 'pm_demo_visa',
        brand: 'Visa',
        last4: '4242',
        expMonth: 12,
        expYear: 2028,
        isDefault: true,
      },
    ],
    invoices: buildDemoBillingInvoices({ now }),
    payments: buildDemoPlatformCharges(100, { now, withinDays: 30 }),
  };

  const existingInvoices = retainPlatformInvoices(billing?.invoices || [], { now });
  const existingPayments = Array.isArray(billing?.payments) ? billing.payments : [];
  const legacyShortDemo =
    existingInvoices.length > 0 &&
    existingInvoices.length <= 2 &&
    existingInvoices.every((inv) => /^inv_2026_/.test(String(inv.id || '')));
  const legacyMonthlyOnlyDemo =
    existingInvoices.length > 0 &&
    existingInvoices.every((inv) => /^inv_demo_/.test(String(inv.id || ''))) &&
    !existingInvoices.some((inv) => inv.kind === 'demand' || inv.demandId);
  const needsInvoiceSeed =
    !existingInvoices.length ||
    legacyShortDemo ||
    legacyMonthlyOnlyDemo ||
    !hasHeavyInvoiceReceiptSeed(existingInvoices);
  const recentCount = filterRecentPlatformCharges(existingPayments, { now }).length;
  const hasOverageCharge = existingPayments.some((payment) =>
    /overage/i.test(String(payment?.label || ''))
  );
  const needsPaymentsSeed =
    existingPayments.length < 100 ||
    !hasChargeStatusDemoSeed(existingPayments) ||
    recentCount === 0 ||
    hasOverageCharge;

  return {
    ...(billing && typeof billing === 'object' ? billing : {}),
    paymentMethods: Array.isArray(billing?.paymentMethods) && billing.paymentMethods.length
      ? billing.paymentMethods
      : demo.paymentMethods,
    invoices: needsInvoiceSeed ? demo.invoices : existingInvoices,
    payments: needsPaymentsSeed ? demo.payments : existingPayments,
  };
}

/** Local demo invoice import when quickbooks_api is offline */
export function demoSyncInvoices(email) {
  const settings = getLocalSettings(email);
  const choices = demandChoiceFields(settings);
  const imported = [
    {
      ROWID: `m_qb_${Date.now()}_1`,
      Demand_ID: resolveCreateDemandId(),
      Debtor_Name: 'Summit Freight Co.',
      Debtor_State: 'TX',
      Amount_Owed: 9750,
      Status: 'INTAKE_VALIDATED',
      Origin: 'AUTOMATED',
      Entry_Tag: 'Automated',
      External_Invoice_ID: 'demo-realm:9002',
      Evidence_Vault_URL: 'quickbooks://invoice/demo-realm/9002',
      ...choices,
    },
    {
      ROWID: `m_qb_${Date.now()}_2`,
      Demand_ID: resolveCreateDemandId(),
      Debtor_Name: 'Lakeside Outfitters',
      Debtor_State: 'NY',
      Amount_Owed: 4200,
      Status: 'INTAKE_VALIDATED',
      Origin: 'AUTOMATED',
      Entry_Tag: 'Automated',
      External_Invoice_ID: 'demo-realm:9003',
      Evidence_Vault_URL: 'quickbooks://invoice/demo-realm/9003',
      ...choices,
    },
  ];
  const next = saveLocalSettings(email, {
    ...settings,
    accountingLastSyncAt: new Date().toISOString(),
  });
  return {
    mock: true,
    imported: imported.length,
    skipped: 0,
    matters: imported,
    lastSyncAt: next.accountingLastSyncAt,
  };
}
